// ./src/styles/theme/fonts.ts
// ============================================
// FormSubmits Typography System
// Aligned with landing website design system
// ============================================

export const fonts = {
  // ============================================
  // Font Families
  // ============================================

  // Space Grotesk - Headings
  // Geometric, technical, distinctive - gives modern tech feel
  display: "'Space Grotesk', -apple-system, BlinkMacSystemFont, sans-serif",

  // IBM Plex Sans - Body text
  // Clean, readable, professional
  primary:
    "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",

  // IBM Plex Mono - Code blocks
  // Consistent with Plex family
  monospace: "'IBM Plex Mono', 'JetBrains Mono', monospace",

  // ============================================
  // Font Sizes (using modular scale)
  // ============================================
  sizes: {
    xs: "0.75rem", // 12px
    sm: "0.875rem", // 14px
    md: "1rem", // 16px (base)
    lg: "1.125rem", // 18px
    xl: "1.25rem", // 20px
    "2xl": "1.5rem", // 24px
    "3xl": "2rem", // 32px
    "4xl": "2.5rem", // 40px
    "5xl": "3.5rem", // 56px
    "6xl": "4.5rem", // 72px

    // Semantic aliases for headings
    h1: "3.5rem", // 56px
    h2: "2.25rem", // 36px
    h3: "1.5rem", // 24px
    h4: "1.25rem", // 20px
    h5: "1rem", // 16px
    h6: "0.875rem", // 14px
  },

  // ============================================
  // Font Weights
  // ============================================
  weights: {
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
  },

  // ============================================
  // Line Heights
  // ============================================
  lineHeights: {
    tight: 1.1,
    snug: 1.25,
    normal: 1.5,
    relaxed: 1.625,
    loose: 2,
  },
} as const;

// ============================================
// Text Style Presets
// Common combinations for consistent styling
// ============================================
export const textStyles = {
  // Body text variants
  bodySmall: {
    fontFamily: fonts.primary,
    fontSize: fonts.sizes.sm,
    lineHeight: fonts.lineHeights.normal,
    fontWeight: fonts.weights.normal,
  },

  bodyDefault: {
    fontFamily: fonts.primary,
    fontSize: fonts.sizes.md,
    lineHeight: fonts.lineHeights.normal,
    fontWeight: fonts.weights.normal,
  },

  bodyLarge: {
    fontFamily: fonts.primary,
    fontSize: fonts.sizes.lg,
    lineHeight: fonts.lineHeights.normal,
    fontWeight: fonts.weights.normal,
  },

  // UI text
  label: {
    fontFamily: fonts.primary,
    fontSize: fonts.sizes.sm,
    lineHeight: fonts.lineHeights.snug,
    fontWeight: fonts.weights.medium,
  },

  caption: {
    fontFamily: fonts.primary,
    fontSize: fonts.sizes.xs,
    lineHeight: fonts.lineHeights.normal,
    fontWeight: fonts.weights.normal,
  },

  // Button text
  button: {
    fontFamily: fonts.primary,
    fontSize: fonts.sizes.md,
    lineHeight: fonts.lineHeights.snug,
    fontWeight: fonts.weights.medium,
  },

  buttonSmall: {
    fontFamily: fonts.primary,
    fontSize: fonts.sizes.sm,
    lineHeight: fonts.lineHeights.snug,
    fontWeight: fonts.weights.medium,
  },

  // Headings (using Space Grotesk)
  heading1: {
    fontFamily: fonts.display,
    fontSize: fonts.sizes.h1,
    lineHeight: fonts.lineHeights.tight,
    fontWeight: fonts.weights.extrabold,
  },

  heading2: {
    fontFamily: fonts.display,
    fontSize: fonts.sizes.h2,
    lineHeight: fonts.lineHeights.snug,
    fontWeight: fonts.weights.bold,
  },

  heading3: {
    fontFamily: fonts.display,
    fontSize: fonts.sizes.h3,
    lineHeight: fonts.lineHeights.snug,
    fontWeight: fonts.weights.semibold,
  },

  heading4: {
    fontFamily: fonts.display,
    fontSize: fonts.sizes.h4,
    lineHeight: fonts.lineHeights.normal,
    fontWeight: fonts.weights.semibold,
  },

  // Code
  code: {
    fontFamily: fonts.monospace,
    fontSize: fonts.sizes.sm,
    lineHeight: fonts.lineHeights.normal,
    fontWeight: fonts.weights.normal,
  },

  codeBlock: {
    fontFamily: fonts.monospace,
    fontSize: fonts.sizes.sm,
    lineHeight: fonts.lineHeights.relaxed,
    fontWeight: fonts.weights.normal,
  },
} as const;
